/**
 * CampaignRankingContentRemapper
 *
 * Bridges raw Firestore JSON → CampaignRankingContentDto → CampaignRankingContentEntity.
 * Flutter source: lib/data/models/campaign_ranking_content.dart (+ .g.dart).
 *
 * Two raw entry points mirror Flutter's dual factories, because the live `media`
 * collection-group doc and the frozen `ranking` subcollection doc differ:
 *  - `fromMediaJson`   ↔ Dart `CampaignRankingContent.fromMedia` (campaign_ranking_content.dart:27):
 *                        reads `firebaseId` (camelCase), `path`, `thumbnail`; no `winner` → `false`.
 *  - `fromRankingJson` ↔ Dart `CampaignRankingContent.fromJson` (.g.dart:9):
 *                        reads `firebase_id`, `path`, `thumbnail`, `winner` (default `false`).
 *
 * `fromDto` maps `path` → `relativePath` and `thumbnail` → `relativeThumbnailPath`
 * (Flutter @JsonKey). The CDN-prefixed URLs (Dart's `path`/`thumbnail` getters
 * prepending `Env.cdnName`) are built by the UI from these relative paths.
 */
import type { CampaignRankingContentDto } from '@/data/dtos/campaign/campaignRankingContentDto';
import type { JsonObject } from '@/data/types/json';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';

export class CampaignRankingContentRemapper {
  /**
   * Live `media` collection-group doc shape (Dart `CampaignRankingContent.fromMedia`).
   * Reads `firebaseId` (camelCase), no `winner` field → `winner = false`.
   */
  static fromMediaJson(data: JsonObject): CampaignRankingContentDto {
    return {
      firebase_id: (data['firebaseId'] as string | undefined) ?? '',
      aspectRatio: (data['aspectRatio'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      coins: (data['coins'] as number | undefined) ?? 0,
      comments: (data['comments'] as number | undefined) ?? 0,
      userName: (data['userName'] as string | undefined) ?? '',
      path: (data['path'] as string | undefined) ?? '',
      thumbnail: (data['thumbnail'] as string | undefined) ?? '',
      winner: false,
    };
  }

  /**
   * Frozen `ranking` subcollection doc shape (Dart `CampaignRankingContent.fromJson`).
   * Reads `firebase_id` (snake_case) and `winner` (default `false`).
   */
  static fromRankingJson(data: JsonObject): CampaignRankingContentDto {
    return {
      firebase_id: (data['firebase_id'] as string | undefined) ?? '',
      aspectRatio: (data['aspectRatio'] as number | undefined) ?? 0,
      likes: (data['likes'] as number | undefined) ?? 0,
      coins: (data['coins'] as number | undefined) ?? 0,
      comments: (data['comments'] as number | undefined) ?? 0,
      userName: (data['userName'] as string | undefined) ?? '',
      path: (data['path'] as string | undefined) ?? '',
      thumbnail: (data['thumbnail'] as string | undefined) ?? '',
      winner: (data['winner'] as boolean | undefined) ?? false,
    };
  }

  static fromMediaJsonList(dataList: JsonObject[]): CampaignRankingContentDto[] {
    return dataList.map(CampaignRankingContentRemapper.fromMediaJson);
  }

  static fromRankingJsonList(dataList: JsonObject[]): CampaignRankingContentDto[] {
    return dataList.map(CampaignRankingContentRemapper.fromRankingJson);
  }

  static fromDto(dto: CampaignRankingContentDto): CampaignRankingContentEntity {
    return {
      firebaseId: dto.firebase_id,
      aspectRatio: dto.aspectRatio,
      likes: dto.likes,
      coins: dto.coins,
      comments: dto.comments,
      userName: dto.userName,
      relativePath: dto.path,
      relativeThumbnailPath: dto.thumbnail,
      winner: dto.winner,
    };
  }
}
